import { Box, FormControl, MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
    perf10kDropdownsGridSx,
    perf10kSelectSx,
} from '../styles/chartReader.styles';
import {
    HintTypography,
    LabelTypography,
    Perf10kSectionTitleTypography,
} from '../styles/chartReader.typography';
import { monthName, parseIsoDate } from '../utils/perf10kFormat';

// Focus the visible Select trigger inside a FormControl. MUI renders it with
// role="combobox" — querying the FormControl wrapper gets us the right node
// regardless of any internal markup shuffling between MUI versions.
function focusSelectTrigger(controlRef: React.RefObject<HTMLDivElement>): void {
    const trigger = controlRef.current?.querySelector<HTMLElement>('[role="combobox"]');
    trigger?.focus();
}

/** Minimum contract every chart-type data point must satisfy to drive the selector. */
export type DateBearingPoint = { date: string };

type DataPointSelectorProps<T extends DateBearingPoint> = {
    /** All points the user can choose from. Order is irrelevant — the selector
     *  derives Year/Month/Date options by parsing `date`. */
    points: T[];
    /** Prefix for the Year/Month/Date `<select>` IDs so multiple instances on a
     *  page (or future chart types) never collide. E.g. `"perf10k"`, `"shout_tna"`. */
    idPrefix: string;
    /** Called only when the user completes a Date selection. Intermediate
     *  Year/Month picks reset the lower dropdowns visually but DO NOT fire this
     *  callback, so the parent's summary stays frozen at the last confirmed
     *  point — NVDA only re-announces once a new Date lands. */
    onSelectedPointChange: (point: T) => void;
};

const menuPaperSx = {
    border: '1px solid #E4E4E4',
    borderRadius: '4px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
};

const MENU_PROPS = { PaperProps: { sx: menuPaperSx } };

/** Pair every point with its parsed parts once — used by all three dropdowns. */
function parsePoints<T extends DateBearingPoint>(points: T[]) {
    const parsed: Array<{ point: T; year: number; month: number; day: number }> = [];
    for (const point of points) {
        const parts = parseIsoDate(point.date);
        if (parts) parsed.push({ point, ...parts });
    }
    return parsed;
}

/**
 * Chart-type-agnostic Year/Month/Date dropdown selector. Any chart whose data
 * points carry a `date: string` ('YYYY-MM-DD') can drop this in — the selector
 * itself never touches the rest of the point shape, it just hands the picked
 * point back to the parent via `onSelectedPointChange`.
 */
export function DataPointSelector<T extends DateBearingPoint>({
    points,
    idPrefix,
    onSelectedPointChange,
}: DataPointSelectorProps<T>) {
    const [year, setYear] = useState<number | ''>('');
    const [month, setMonth] = useState<number | ''>('');
    const [isoDate, setIsoDate] = useState<string>('');

    const yearControlRef = useRef<HTMLDivElement>(null);
    const monthControlRef = useRef<HTMLDivElement>(null);
    const dateControlRef = useRef<HTMLDivElement>(null);

    const parsedPoints = useMemo(() => parsePoints(points), [points]);

    const yearOptions = useMemo(() => {
        const years = new Set<number>();
        for (const p of parsedPoints) years.add(p.year);
        // Descending — most recent years first, since users typically reach for
        // the latest data point.
        return Array.from(years).sort((a, b) => b - a);
    }, [parsedPoints]);

    const monthOptions = useMemo(() => {
        if (year === '') return [];
        const months = new Set<number>();
        for (const p of parsedPoints) {
            if (p.year === year) months.add(p.month);
        }
        return Array.from(months).sort((a, b) => a - b);
    }, [parsedPoints, year]);

    /** [{ point, day }] for the selected year+month, sorted by day ascending. */
    const dateOptions = useMemo(() => {
        if (year === '' || month === '') return [];
        return parsedPoints
            .filter((p) => p.year === year && p.month === month)
            .sort((a, b) => a.day - b.day)
            .map((p) => ({ point: p.point, day: p.day }));
    }, [parsedPoints, year, month]);

    const handleYearChange = useCallback(
        (event: SelectChangeEvent<number | ''>) => {
            const next = event.target.value as number;
            setYear(next);
            setMonth('');
            setIsoDate('');
            // Intentionally NOT calling onSelectedPointChange here — the parent's
            // last confirmed selection stays in effect so NVDA doesn't re-announce
            // the default summary every time the user starts a fresh date pick.
            // Wait for React to flush the disabled→enabled state change on Month
            // before focusing — disabled triggers can't receive focus.
            setTimeout(() => focusSelectTrigger(monthControlRef), 50);
        },
        [],
    );

    const handleMonthChange = useCallback(
        (event: SelectChangeEvent<number | ''>) => {
            const next = event.target.value as number;
            setMonth(next);
            setIsoDate('');
            // See handleYearChange — no callback fire on intermediate Month pick.
            setTimeout(() => focusSelectTrigger(dateControlRef), 50);
        },
        [],
    );

    const handleDateChange = useCallback(
        (event: SelectChangeEvent<string>) => {
            const next = event.target.value;
            setIsoDate(next);
            const match = dateOptions.find((d) => d.point.date === next);
            if (match) onSelectedPointChange(match.point);
            // Don't move focus on selection — the panel's live region re-announces
            // the updated summary while focus stays on Date so the user can hear
            // the result before deciding to Tab back to Year.
        },
        [dateOptions, onSelectedPointChange],
    );

    // Tab from the Date trigger cycles back to Year — but only after a date
    // has been picked. Without a selection, plain Tab still moves forward in
    // document order so the user can leave the selector normally. Shift+Tab
    // always goes backward.
    const handleDateKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Tab' && !event.shiftKey && isoDate) {
                event.preventDefault();
                focusSelectTrigger(yearControlRef);
            }
        },
        [isoDate],
    );

    /** Day-of-month for the currently-selected ISO date, for the collapsed Select label. */
    const selectedDay = useMemo(() => {
        if (!isoDate) return null;
        return parseIsoDate(isoDate)?.day ?? null;
    }, [isoDate]);

    const isMonthDisabled = year === '' || monthOptions.length === 0;
    const isDateDisabled = month === '' || dateOptions.length === 0;

    const yearId = `${idPrefix}-year-select`;
    const monthId = `${idPrefix}-month-select`;
    const dateId = `${idPrefix}-date-select`;

    return (
        <Box>
            <Perf10kSectionTitleTypography>Read a specific data point</Perf10kSectionTitleTypography>
            <HintTypography sx={{ mt: 0.5 }}>Select an option to hear the value.</HintTypography>

            <Box sx={perf10kDropdownsGridSx}>
                {/* Year */}
                <Box>
                    <label htmlFor={yearId}>
                        <LabelTypography sx={{ mb: 0.5 }}>Year</LabelTypography>
                    </label>
                    <FormControl ref={yearControlRef} fullWidth size='small' sx={perf10kSelectSx}>
                        <Select<number | ''>
                            id={yearId}
                            value={year}
                            onChange={handleYearChange}
                            displayEmpty
                            renderValue={(v) => (typeof v === 'number' ? String(v) : 'Select year')}
                            inputProps={{ 'aria-label': year === '' ? 'Select year' : 'Year' }}
                            MenuProps={MENU_PROPS}>
                            {yearOptions.map((y) => (
                                <MenuItem key={y} value={y}>
                                    {y}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Month */}
                <Box>
                    <label htmlFor={monthId}>
                        <LabelTypography sx={{ mb: 0.5 }}>Month</LabelTypography>
                    </label>
                    <FormControl
                        ref={monthControlRef}
                        fullWidth
                        size='small'
                        disabled={isMonthDisabled}
                        sx={perf10kSelectSx}>
                        <Select<number | ''>
                            id={monthId}
                            value={month}
                            onChange={handleMonthChange}
                            displayEmpty
                            renderValue={(v) => (typeof v === 'number' ? monthName(v) : 'Select month')}
                            inputProps={{ 'aria-label': month === '' ? 'Select month' : 'Month' }}
                            MenuProps={MENU_PROPS}>
                            {monthOptions.map((m) => (
                                <MenuItem key={m} value={m}>
                                    {monthName(m)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Date — day number only */}
                <Box onKeyDown={handleDateKeyDown}>
                    <label htmlFor={dateId}>
                        <LabelTypography sx={{ mb: 0.5 }}>Date</LabelTypography>
                    </label>
                    <FormControl
                        ref={dateControlRef}
                        fullWidth
                        size='small'
                        disabled={isDateDisabled}
                        sx={perf10kSelectSx}>
                        <Select
                            id={dateId}
                            value={isoDate}
                            onChange={handleDateChange}
                            displayEmpty
                            renderValue={() => (selectedDay !== null ? String(selectedDay) : 'Select date')}
                            inputProps={{ 'aria-label': isoDate === '' ? 'Select date' : 'Date' }}
                            MenuProps={MENU_PROPS}>
                            {dateOptions.map(({ point, day }) => (
                                <MenuItem key={point.date} value={point.date}>
                                    {day}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>
        </Box>
    );
}
